/** PLANȘA ARHITECTULUI desenată sub geometria editorului — v. `desenPlansa`
 *  pentru ce e și de unde vine. Aici doar SVG-ul: traseele în unitățile foii,
 *  sub o singură matrice, cu liniile de grosimea lor pe ecran
 *  (`non-scaling-stroke`), ca în vizorul planșelor. */
import { useMemo } from "react";
import { fetchDrawingUrl, fileDrawingUrl } from "./apiStructura";
import type { Crs } from "./calcArhitectura";
import { useCerut, type Cerut } from "./cerere";
import { displayColor } from "./culoriDesen";
import type { PlansaImport, PlanseImport } from "../nav/api";
import { decupeaza, matriceaPlansei, plansaStearsa, traseele,
         type ImportulPlansei } from "./desenPlansa";
import type { Cadru } from "./editorGeometrie";
import type { DrawingData, DrawingText } from "./tipuri";

/** Desenul foii din care a venit nivelul (`importul`), cerut o dată pe fișier
 *  (`cerere.useCerut`, peste memoria desenelor din `apiStructura`); nimic
 *  cât `importul` e `null`. Foaia ștearsă din dosar (`planse`, semnul
 *  registrului pe arhitectură) nu se cere: `stearsa` o numește, ca editorul
 *  să spună de ce arată planul documentului. */
export function useDesenPlansa(projectId: string, importul: ImportulPlansei | null,
                               planse?: PlanseImport | null):
    Cerut<DrawingData> & { stearsa: PlansaImport | null } {
  const stearsa = plansaStearsa(importul, planse);
  const fid = stearsa ? null : importul?.fisierId ?? null;
  const cerut = useCerut<DrawingData>(
    fid === null ? null : `${projectId}/${fid}`,
    () => fetchDrawingUrl(fileDrawingUrl(projectId, fid as string)));
  return { ...cerut, stearsa };
}

/** Sub câți pixeli un text nu se mai desenează: oricum nu se citește. */
const TEXT_PX_MIN = 3;

/** Un text al foii, în cadrul ei: `rotate` e al foii (antiorar), iar
 *  `scale(1 -1)` întoarce glifele, desenate de SVG cu Y-ul în jos, într-un
 *  cadru cu Y-ul în sus. Punctul de prindere (1..9, ca MTEXT) alege
 *  ancora și rândul de referință. */
function Text({ e }: { e: DrawingText }) {
  const col = (e.a - 1) % 3;
  const rand = Math.floor((e.a - 1) / 3);
  const linii = e.s.split("\n");
  const lh = e.h * 1.25;
  const y = (i: number) =>
    rand === 0 ? i * lh + e.h
    : rand === 1 ? (i - (linii.length - 1) / 2) * lh + e.h * 0.35
    : (i - (linii.length - 1)) * lh;
  return (
    <text transform={`translate(${e.x} ${e.y}) rotate(${e.r}) scale(1 -1)`}
          fontSize={e.h} fill={displayColor(e.c)}
          textAnchor={col === 0 ? "start" : col === 1 ? "middle" : "end"}
          fontFamily="'IBM Plex Sans', sans-serif">
      {linii.map((l, i) => <tspan key={i} x={0} y={y(i)}>{l}</tspan>)}
    </text>
  );
}

export function PlansaArhitect({ desen, importul, crs, thetaGrade, cadru, pxPerM }: {
  desen: DrawingData; importul: ImportulPlansei; crs: Crs;
  thetaGrade: number; cadru: Cadru; pxPerM: number;
}) {
  const entitati = useMemo(() => decupeaza(desen.entities, importul), [desen, importul]);
  const trasee = useMemo(() => traseele(desen, entitati, importul.factor),
                         [desen, entitati, importul.factor]);
  const matrice = useMemo(() => matriceaPlansei(importul, crs, thetaGrade, cadru),
                          [importul, crs, thetaGrade, cadru]);
  // înălțimea minimă a textului desenat, în unitățile foii
  const hMin = (TEXT_PX_MIN / pxPerM) * 1000 / importul.factor;
  return (
    <g className="ob-es-plansa" transform={matrice}>
      {trasee.umpleri.map((u, i) => (
        <path key={`u${i}`} d={u.d} fill={u.culoare} fillRule="evenodd" stroke="none" />
      ))}
      {trasee.hasuri.map((h, i) => (
        <path key={`h${i}`} d={h.d} fill="none" stroke={h.culoare} strokeWidth={1}
              strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      ))}
      {trasee.linii.map((l, i) => (
        <path key={`l${i}`} d={l.d} fill="none" stroke={l.culoare} strokeWidth={l.latime}
              strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      ))}
      {trasee.texte.filter((e) => e.h >= hMin).map((e, i) => <Text key={`t${i}`} e={e} />)}
    </g>
  );
}
